import { motion } from 'framer-motion';
import { cn } from '../../utils/cn';

/**
 * Unified Card Component
 * 
 * Enforces consistent card styling across the app with optional glass morphism.
 * 
 * @param {boolean} hover - Enable hover effects (lift + shadow)
 * @param {boolean} glass - Apply glass morphism effect
 * @param {string} className - Additional classes
 * @param {function} onClick - Click handler (enables hover automatically)
 * 
 * Usage:
 * <Card>Content</Card>
 * <Card hover>Content with hover effect</Card>
 * <Card glass>Content with glass morphism</Card>
 * <Card hover onClick={handleClick}>Interactive card</Card>
 */
export function Card({ 
  children, 
  hover = false, 
  glass = false,
  className = '',
  onClick,
  ...props 
}) {
  const baseClasses = 'bg-white rounded-card border border-border';
  const hoverClasses = hover || onClick ? 'hover:shadow-card-hover transition-all cursor-pointer' : 'shadow-card';
  const glassClasses = glass ? 'glass-medium' : '';
  
  const Component = (hover || onClick) ? motion.div : 'div';
  const motionProps = (hover || onClick) ? {
    whileHover: { y: -4 },
    transition: { duration: 0.2 }
  } : {};

  return (
    <Component
      className={cn(baseClasses, hoverClasses, glassClasses, className)}
      onClick={onClick}
      {...motionProps}
      {...props}
    >
      {children}
    </Component>
  );
}
